import { createSecureSmsUri } from "@redotech/phone/number";
import {
  ButtonAction,
  TextToSubscribeButtonElement,
} from "@redotech/redo-content-builder-common/element-schema";
import { SUBSCRIBE_MESSAGE } from "@redotech/redo-marketing-common/signup-form/signup-form";
import { BrandKitButton } from "@redotech/redo-web/brand-kit-button";
import { memo, MouseEvent, useCallback } from "react";
import { getBaseElementStyles } from "./base-element-styles";
import { FormElementProps } from "./index";

export const TextToSubscribeButton = memo(function TextToSubscribeButton({
  element,
  brandKit,
  brandKitOverrides,
  merchantPhoneNumber,
  anonymousId,
  submitting,
  isBuilder,
  onCreatePendingTextToSubscribeRecord,
}: Pick<
  FormElementProps<TextToSubscribeButtonElement>,
  | "element"
  | "brandKit"
  | "brandKitOverrides"
  | "merchantPhoneNumber"
  | "anonymousId"
  | "submitting"
  | "isBuilder"
  | "onCreatePendingTextToSubscribeRecord"
>) {
  const handleClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      if (isBuilder) {
        return;
      }

      // Capture the form reference synchronously — currentTarget is nulled
      // after the synchronous handler returns.
      const form = event.currentTarget.form;

      let lookupId: string | undefined;
      if (onCreatePendingTextToSubscribeRecord) {
        try {
          lookupId = await onCreatePendingTextToSubscribeRecord();
        } catch {
          // Silently fall back to anonymousId
        }
      }

      const finalId = lookupId ?? anonymousId;

      if (merchantPhoneNumber && finalId) {
        const smsBody = `${SUBSCRIBE_MESSAGE} ID: ${finalId}`;
        const smsUri = createSecureSmsUri({
          phoneNumbers: [merchantPhoneNumber],
          body: smsBody,
        });
        if (smsUri) {
          window.location.href = smsUri;
        }
      }

      if (element.action === ButtonAction.SUBMIT_AND_ADVANCE && form) {
        form.requestSubmit();
      }
    },
    [
      isBuilder,
      onCreatePendingTextToSubscribeRecord,
      anonymousId,
      merchantPhoneNumber,
      element.action,
    ],
  );

  const hasBaseStyles =
    element.backgroundColor !== undefined ||
    element.borderWidth !== undefined ||
    element.padding !== undefined;

  const button = (
    <BrandKitButton
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      globalOverride={
        brandKitOverrides?.buttons.enabled
          ? brandKitOverrides?.buttons
          : undefined
      }
      hierarchy={element.hierarchy}
      onClick={handleClick}
      pending={submitting}
      text={element.text}
      type="button"
    />
  );

  if (!hasBaseStyles) {
    return button;
  }

  return (
    <div style={{ ...getBaseElementStyles(element), width: "100%" }}>
      {button}
    </div>
  );
});
